import { createFileRoute } from '@tanstack/react-router'
import { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { useQuery as useConvexQuery, useAction, useMutation } from 'convex/react'
import { api } from 'convex/_generated/api'
import { useCurrentUser } from '@/hooks/useCurrentUser'
import type { Id } from 'convex/_generated/dataModel'
import { FlashcardCard } from '@/components/FlashcardCard'
import { SessionHistorySidebar } from '@/components/SessionHistorySidebar'
import { useFlashcardAudioPreloader } from '@/hooks/useFlashcardAudioPreloader'
import { usePostHog } from '@/hooks/usePostHog'
import { History, Music } from 'lucide-react'
import { Link } from '@tanstack/react-router'
import { WaveSurferPlayer, type FlashcardRegion } from '@/components/WaveSurferPlayer'
import { useAppSelector, useAppDispatch } from '@/hooks/useRedux'
import {
  selectPlaybackMode,
  setPlaybackMode,
} from '@/store/slices/flashcardPlaybackSlice'

export const Route = createFileRoute('/app/flashcards')({
  component: FlashcardsPage,
})

type AnkiGrade = 0 | 1 | 2 | 3 | 4 | 5

function FlashcardsPage() {
  const currentUser = useCurrentUser()

  if (currentUser === undefined) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-sky-blue text-xl font-mono">Loading...</div>
      </div>
    )
  }

  if (currentUser === null) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-trail-tan text-xl font-mono">Please sign in to review flashcards</div>
      </div>
    )
  }

  return <FlashcardsContent userId={currentUser._id} />
}

function FlashcardsContent({ userId }: { userId: Id<'users'> }) {
  const [currentIndex, setCurrentIndex] = useState(0)
  const [isFlipped, setIsFlipped] = useState(false)
  const [reviewStartTime, setReviewStartTime] = useState<number | null>(null)
  const [elapsedTime, setElapsedTime] = useState(0)
  const [sessionStartTime] = useState(Date.now())
  const [isAnkiInfoExpanded, setIsAnkiInfoExpanded] = useState(false)
  const [cardsReviewed, setCardsReviewed] = useState(0)
  const [sessionGrades, setSessionGrades] = useState<AnkiGrade[]>([])
  const [isSidebarOpen, setIsSidebarOpen] = useState(false)
  const [selectedDeckIds, setSelectedDeckIds] = useState<Id<'flashcardDecks'>[] | undefined>(undefined) // undefined = all active decks
  const intervalRef = useRef<number | null>(null)

  // Redux state for playback mode
  const dispatch = useAppDispatch()
  const playbackMode = useAppSelector(selectPlaybackMode)

  const submitReview = useAction(api.flashcards.submitFlashcardReview)
  const createSession = useMutation(api.flashcards.createSession)
  const { trackEvent } = usePostHog()

  // Get all decks to check if there are active decks
  const allDecks = useConvexQuery(api.flashcards.getAllDecksWithCounts, { userId })

  // Get due flashcards with audio URLs, filtered by selected decks
  const dueFlashcards = useConvexQuery(api.flashcards.getDueFlashcardsByDecks, {
    userId,
    deckIds: selectedDeckIds,
    limit: 50,
  })

  // Get due flashcards from ALL decks (to check if other decks have cards)
  const allDueFlashcards = useConvexQuery(api.flashcards.getDueFlashcardsByDecks, {
    userId,
    deckIds: undefined, // All active decks
    limit: 50,
  })

  // Get upcoming flashcard counts for dashboard
  const tomorrowCount = useConvexQuery(api.flashcards.getUpcomingFlashcardsCount, {
    userId,
    days: 1,
  })
  const weekCount = useConvexQuery(api.flashcards.getUpcomingFlashcardsCount, {
    userId,
    days: 7,
  })

  // Get today's session statistics for dashboard
  const todayStats = useConvexQuery(api.flashcards.getTodaySessionStats, {
    userId,
  })

  // Preload audio for upcoming flashcards (runs in background)
  useFlashcardAudioPreloader(dueFlashcards || [], currentIndex, 2)

  // Timer effect - updates every second
  useEffect(() => {
    if (reviewStartTime !== null) {
      intervalRef.current = window.setInterval(() => {
        setElapsedTime(Math.floor((Date.now() - reviewStartTime) / 1000))
      }, 1000)

      return () => {
        if (intervalRef.current !== null) {
          clearInterval(intervalRef.current)
        }
      }
    }
  }, [reviewStartTime])

  // Reset timer when card changes
  useEffect(() => {
    if (dueFlashcards && dueFlashcards.length > 0 && currentIndex < dueFlashcards.length) {
      setReviewStartTime(Date.now())
      setElapsedTime(0)
      setIsFlipped(false)
    }
  }, [currentIndex, dueFlashcards])

  // Sync currentIndex with array length to prevent race condition bugs
  // When Convex query removes reviewed card, this ensures index stays in bounds
  useEffect(() => {
    if (dueFlashcards && currentIndex >= dueFlashcards.length && dueFlashcards.length > 0) {
      setCurrentIndex(dueFlashcards.length - 1)
    }
  }, [dueFlashcards, currentIndex])

  // Safe boundary check to prevent "Card 3 of 2" bug when Convex query updates
  const safeCurrentIndex = Math.min(currentIndex, Math.max(0, (dueFlashcards?.length ?? 1) - 1))
  const currentCard = dueFlashcards && dueFlashcards.length > 0 ? dueFlashcards[safeCurrentIndex] : null

  // Memoize regions to prevent infinite re-renders (stable array reference)
  const audioRegions = useMemo<FlashcardRegion[]>(() => {
    if (!currentCard?.audioTimestamp) return []

    // Helper: Calculate review score from easeFactor (1.3-2.5) → (0-5)
    const deriveReviewScore = (card: typeof currentCard): number | undefined => {
      if (!card.lastReviewDate) return undefined
      return Math.min(5, Math.max(0, Math.round((card.easeFactor - 1.3) / 1.2 * 5)))
    }

    // WHOLE SONG MODE: Show all flashcards from this song
    if (playbackMode === 'wholeSong') {
      const sameSongCards = dueFlashcards?.filter(card =>
        currentCard.sourceSongId && // Ensure current card has sourceSongId
        card.sourceSongId === currentCard.sourceSongId &&
        card.audioTimestamp
      ) || []

      return sameSongCards.map(card => ({
        id: `flashcard-${card._id}`,
        start: card.audioTimestamp!.startTime * 1000,
        end: card.audioTimestamp!.endTime * 1000,
        text: card.frontSpanish.join(' '),
        reviewScore: deriveReviewScore(card),
      }))
    }

    // VOCABULARY MODE: Single region with dynamic end-time
    const nextCard = dueFlashcards?.[safeCurrentIndex + 1]
    const isSameSong = currentCard.sourceSongId &&
                       nextCard?.sourceSongId === currentCard.sourceSongId

    // Use next card's start as end if same song, otherwise use original end
    const endTime = (isSameSong && nextCard?.audioTimestamp)
      ? nextCard.audioTimestamp.startTime * 1000
      : currentCard.audioTimestamp.endTime * 1000

    return [{
      id: `flashcard-${currentCard._id}`,
      start: currentCard.audioTimestamp.startTime * 1000,
      end: endTime,
      text: currentCard.frontSpanish.join(' '),
      color: '#00ffff',
    }]
  }, [playbackMode, currentCard, dueFlashcards, safeCurrentIndex])

  // Memoize region click handler (stable function reference)
  const handleRegionClick = useCallback((region: FlashcardRegion) => {
    console.log('Region clicked:', region)
  }, [])

  // Get source song data for current flashcard
  const sourceSongData = useConvexQuery(
    api.flashcards.getFlashcardSourceSong,
    currentCard?._id ? { flashcardId: currentCard._id } : 'skip'
  )

  const handleGrade = useCallback(
    async (grade: AnkiGrade) => {
      if (!currentCard) return

      try {
        await submitReview({
          flashcardId: currentCard._id,
          grade,
        })

        // Track flashcard review event
        trackEvent('flashcard_reviewed', {
          grade,
          cardType: currentCard.cardType,
        })

        const newCardsReviewed = cardsReviewed + 1
        const newSessionGrades = [...sessionGrades, grade]
        setCardsReviewed(newCardsReviewed)
        setSessionGrades(newSessionGrades)

        // Move to next card
        if (currentIndex < (dueFlashcards?.length ?? 0) - 1) {
          setCurrentIndex(currentIndex + 1)
        } else {
          // All cards reviewed - create session record and track completion
          const sessionDuration = Math.floor((Date.now() - sessionStartTime) / 1000)

          // Create session record in database
          await createSession({
            userId,
            duration: sessionDuration,
            cardsReviewed: newCardsReviewed,
            grades: newSessionGrades,
            startedAt: sessionStartTime,
          })

          trackEvent('flashcard_session_completed', {
            sessionCardsReviewed: newCardsReviewed,
            sessionDuration,
          })

          // Reset for next session
          setCurrentIndex(0)
          setSessionGrades([])
          setCardsReviewed(0)
        }
      } catch (error) {
        console.error('Failed to submit review:', error)
      }
    },
    [currentCard, currentIndex, dueFlashcards, submitReview, createSession, trackEvent, cardsReviewed, sessionGrades, sessionStartTime, userId]
  )

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  const formatSessionTime = (startTime: number) => {
    const totalSeconds = Math.floor((Date.now() - startTime) / 1000)
    return formatTime(totalSeconds)
  }

  if (!dueFlashcards) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-sky-blue text-xl font-mono">Loading flashcards...</div>
      </div>
    )
  }

  if (dueFlashcards.length === 0) {
    // Check if all decks are paused
    const activeDecks = allDecks?.filter(deck => (deck.status ?? 'active') === 'active') ?? []
    const hasActiveDecks = activeDecks.length > 0

    // Check if a specific deck is selected and other decks have cards
    const isSpecificDeckSelected = selectedDeckIds !== undefined && selectedDeckIds.length > 0
    const otherDecksHaveCards = allDueFlashcards && allDueFlashcards.length > 0

    // If specific deck is caught up but other decks have cards, show main component with message
    if (isSpecificDeckSelected && otherDecksHaveCards) {
      // Continue to main component rendering with "caught up for this deck" message
      // (handled below after this entire if block by NOT returning here)
    } else {
      // All decks are caught up or paused - show full empty state
      if (!hasActiveDecks && allDecks && allDecks.length > 0) {
        // All decks are paused - show paused deck message
        return (
          <div className="min-h-screen bg-black text-white py-8 px-4">
            <div className="max-w-4xl mx-auto space-y-6">
              {/* Header */}
              <div className="terminal-border border-trail-tan p-8 text-center">
                <h1 className="text-3xl font-bold text-trail-tan mb-4 font-mono tracking-wider">
                  ⏸️ [ALL DECKS PAUSED]
                </h1>
              <p className="text-trail-tan font-mono text-lg mb-4">
                All your flashcard decks are currently paused.
              </p>
              <p className="text-sky-blue font-mono text-sm">
                Activate a deck to continue your learning journey!
              </p>
            </div>

            {/* Today's Stats (if any) */}
            {todayStats && todayStats.sessionsCount > 0 && (
              <div className="terminal-border border-sky-blue p-6">
                <h2 className="text-xl font-bold text-sky-blue mb-4 font-mono tracking-wider">
                  [TODAY'S PROGRESS]
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="bg-black border-2 border-terminal-green p-4">
                    <div className="text-terminal-green text-xs font-mono mb-1 uppercase tracking-wider">
                      Cards Reviewed
                    </div>
                    <div className="text-terminal-green text-3xl font-bold font-mono">
                      {todayStats.totalCardsReviewed}
                    </div>
                  </div>
                  <div className="bg-black border-2 border-sky-blue p-4">
                    <div className="text-sky-blue text-xs font-mono mb-1 uppercase tracking-wider">
                      Session Time
                    </div>
                    <div className="text-sky-blue text-3xl font-bold font-mono">
                      {Math.floor(todayStats.totalDuration / 60)}m
                    </div>
                  </div>
                  <div className="bg-black border-2 border-trail-tan p-4">
                    <div className="text-trail-tan text-xs font-mono mb-1 uppercase tracking-wider">
                      Avg. Confidence
                    </div>
                    <div className="text-trail-tan text-3xl font-bold font-mono">
                      {todayStats.averageGrade.toFixed(1)}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Manage Decks CTA */}
            <div className="terminal-border border-terminal-green p-6 bg-black">
              <h3 className="text-lg font-bold text-terminal-green mb-3 font-mono tracking-wider">
                📚 [MANAGE YOUR DECKS]
              </h3>
              <div className="space-y-3">
                <p className="text-trail-tan font-mono text-sm leading-relaxed">
                  Visit your decks page to activate the decks you want to study.
                </p>
                <Link
                  to="/app/decks"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-terminal-green/10 border-2 border-terminal-green text-terminal-green hover:bg-terminal-green hover:text-black transition-colors font-mono font-bold"
                >
                  View Decks →
                </Link>
              </div>
            </div>
          </div>
        </div>
      )
      }

      // Has active decks but no due flashcards - show "all caught up" message
      return (
      <div className="min-h-screen bg-black text-white py-8 px-4">
        <div className="max-w-4xl mx-auto space-y-6">
          {/* Header */}
          <div className="terminal-border p-8 text-center">
            <h1 className="text-3xl font-bold text-terminal-green mb-4 font-mono tracking-wider">
              ✅ [ALL CAUGHT UP!]
            </h1>
            <p className="text-trail-tan font-mono text-lg">
              No flashcards due for review right now.
            </p>
          </div>

          {/* Today's Stats */}
          {todayStats && todayStats.sessionsCount > 0 && (
            <div className="terminal-border border-sky-blue p-6">
              <h2 className="text-xl font-bold text-sky-blue mb-4 font-mono tracking-wider">
                [TODAY'S PROGRESS]
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="bg-black border-2 border-terminal-green p-4">
                  <div className="text-terminal-green text-xs font-mono mb-1 uppercase tracking-wider">
                    Cards Reviewed
                  </div>
                  <div className="text-terminal-green text-3xl font-bold font-mono">
                    {todayStats.totalCardsReviewed}
                  </div>
                </div>
                <div className="bg-black border-2 border-sky-blue p-4">
                  <div className="text-sky-blue text-xs font-mono mb-1 uppercase tracking-wider">
                    Session Time
                  </div>
                  <div className="text-sky-blue text-3xl font-bold font-mono">
                    {Math.floor(todayStats.totalDuration / 60)}m
                  </div>
                </div>
                <div className="bg-black border-2 border-trail-tan p-4">
                  <div className="text-trail-tan text-xs font-mono mb-1 uppercase tracking-wider">
                    Avg. Confidence
                  </div>
                  <div className="text-trail-tan text-3xl font-bold font-mono">
                    {todayStats.averageGrade.toFixed(1)}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Upcoming Schedule */}
          <div className="terminal-border border-sky-blue p-6">
            <h2 className="text-xl font-bold text-sky-blue mb-4 font-mono tracking-wider">
              [UPCOMING REVIEWS]
            </h2>
            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 bg-black border-2 border-sky-blue">
                <span className="text-trail-tan font-mono">Tomorrow:</span>
                <span className="text-terminal-green font-mono font-bold text-xl">
                  {tomorrowCount ?? 0} cards
                </span>
              </div>
              <div className="flex items-center justify-between p-3 bg-black border-2 border-sky-blue">
                <span className="text-trail-tan font-mono">Next 7 days:</span>
                <span className="text-terminal-green font-mono font-bold text-xl">
                  {weekCount ?? 0} cards
                </span>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4">
            <button
              onClick={() => setIsSidebarOpen(true)}
              className="flex-1 flex items-center justify-center gap-2 px-6 py-4 bg-sky-blue/10 border-2 border-sky-blue text-sky-blue hover:bg-sky-blue hover:text-black transition-colors font-mono font-bold text-lg"
              type="button"
            >
              <History className="w-5 h-5" />
              <span className="font-bold">View Session History</span>
            </button>
          </div>

          {/* Add More Cards Prompt */}
          <div className="terminal-border border-terminal-green p-6 bg-black">
            <h3 className="text-lg font-bold text-terminal-green mb-3 font-mono tracking-wider">
              🎵 [READY FOR MORE?]
            </h3>
            <div className="space-y-3">
              <p className="text-trail-tan font-mono text-sm leading-relaxed">
                Want to keep learning? Add new vocabulary to your deck from our song library!
              </p>
              <a
                href="/app/songs/library"
                className="inline-flex items-center gap-2 px-6 py-3 bg-terminal-green/10 border-2 border-terminal-green text-terminal-green hover:bg-terminal-green hover:text-black transition-colors font-mono font-bold"
              >
                Browse Song Library →
              </a>
            </div>
          </div>
        </div>

        {/* Session History Sidebar */}
        <SessionHistorySidebar
          userId={userId}
          isOpen={isSidebarOpen}
          onClose={() => setIsSidebarOpen(false)}
        />
      </div>
      )
    }
  }

  // Special case: specific deck is caught up but other decks have cards
  // Show deck selector with "caught up" message instead of full empty state
  const isSpecificDeckSelected = selectedDeckIds !== undefined && selectedDeckIds.length > 0
  const currentDeckCaughtUp = dueFlashcards.length === 0
  const otherDecksHaveCards = allDueFlashcards && allDueFlashcards.length > 0
  const showDeckSelectorWithMessage = isSpecificDeckSelected && currentDeckCaughtUp && otherDecksHaveCards

  if (showDeckSelectorWithMessage) {
    return (
      <div className="min-h-screen bg-black text-white py-4 sm:py-8 px-2 sm:px-4">
        <div className="max-w-4xl mx-auto">
          {/* Header with deck selector */}
          <div className="mb-4 sm:mb-6 terminal-border p-4 sm:p-6">
            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <h1 className="text-2xl sm:text-3xl font-bold text-sky-blue font-mono tracking-wider">
                  [FLASHCARD REVIEW]
                </h1>
                <button
                  onClick={() => setIsSidebarOpen(true)}
                  className="flex items-center gap-2 px-3 py-2 bg-sky-blue/10 border-2 border-sky-blue text-sky-blue hover:bg-sky-blue hover:text-black transition-colors font-mono text-base"
                  type="button"
                >
                  <History className="w-4 h-4" />
                  <span className="hidden sm:inline font-bold">History</span>
                </button>
              </div>

              {/* Deck Selector */}
              {allDecks && allDecks.length > 1 && (
                <div className="mb-3">
                  <label className="block text-sm font-mono text-trail-tan mb-2">
                    [DECK FILTER]
                  </label>
                  <select
                    value={selectedDeckIds === undefined ? 'all' : selectedDeckIds.length === 1 ? selectedDeckIds[0] : 'custom'}
                    onChange={(e) => {
                      const value = e.target.value
                      if (value === 'all') {
                        setSelectedDeckIds(undefined)
                        setCurrentIndex(0)
                      } else {
                        setSelectedDeckIds([value as Id<'flashcardDecks'>])
                        setCurrentIndex(0)
                      }
                    }}
                    className="w-full sm:w-auto px-4 py-2 bg-black border-2 border-sky-blue text-terminal-green font-mono focus:outline-none focus:border-terminal-green"
                  >
                    <option value="all" className="bg-black text-terminal-green">
                      All Active Decks ({allDecks.filter(d => (d.status ?? 'active') === 'active').length})
                    </option>
                    {allDecks
                      .filter(deck => (deck.status ?? 'active') === 'active')
                      .map(deck => (
                        <option key={deck._id} value={deck._id} className="bg-black text-terminal-green">
                          {deck.name} ({deck.flashcardCount || 0} cards)
                        </option>
                      ))}
                  </select>
                </div>
              )}
            </div>
          </div>

          {/* All caught up for this deck message */}
          <div className="terminal-border border-terminal-green p-8 text-center mb-6">
            <h2 className="text-3xl font-bold text-terminal-green mb-4 font-mono tracking-wider">
              ✅ [ALL CAUGHT UP FOR THIS DECK!]
            </h2>
            <p className="text-trail-tan font-mono text-lg mb-4">
              No flashcards due for this deck right now.
            </p>
            <p className="text-sky-blue font-mono text-sm">
              Select a different deck above to continue reviewing!
            </p>
          </div>

          {/* Session History Sidebar */}
          <SessionHistorySidebar
            userId={userId}
            isOpen={isSidebarOpen}
            onClose={() => setIsSidebarOpen(false)}
          />
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-black text-white py-4 sm:py-8 px-2 sm:px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header with progress and timer */}
        <div className="mb-4 sm:mb-6 terminal-border p-4 sm:p-6">
          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <h1 className="text-2xl sm:text-3xl font-bold text-sky-blue font-mono tracking-wider">
                [FLASHCARD REVIEW]
              </h1>
              <button
                onClick={() => setIsSidebarOpen(true)}
                className="flex items-center gap-2 px-3 py-2 bg-sky-blue/10 border-2 border-sky-blue text-sky-blue hover:bg-sky-blue hover:text-black transition-colors font-mono text-base"
                type="button"
              >
                <History className="w-4 h-4" />
                <span className="hidden sm:inline font-bold">History</span>
              </button>
            </div>

            {/* Deck Selector */}
            {allDecks && allDecks.length > 1 && (
              <div className="mb-3">
                <label className="block text-sm font-mono text-trail-tan mb-2">
                  [DECK FILTER]
                </label>
                <select
                  value={selectedDeckIds === undefined ? 'all' : selectedDeckIds.length === 1 ? selectedDeckIds[0] : 'custom'}
                  onChange={(e) => {
                    const value = e.target.value
                    if (value === 'all') {
                      setSelectedDeckIds(undefined)
                      setCurrentIndex(0) // Reset to first card
                    } else {
                      setSelectedDeckIds([value as Id<'flashcardDecks'>])
                      setCurrentIndex(0) // Reset to first card
                    }
                  }}
                  className="w-full sm:w-auto px-4 py-2 bg-black border-2 border-sky-blue text-terminal-green font-mono focus:outline-none focus:border-terminal-green"
                >
                  <option value="all" className="bg-black text-terminal-green">
                    All Active Decks ({allDecks.filter(d => (d.status ?? 'active') === 'active').length})
                  </option>
                  {allDecks
                    .filter(deck => (deck.status ?? 'active') === 'active')
                    .map(deck => (
                      <option key={deck._id} value={deck._id} className="bg-black text-terminal-green">
                        {deck.name} ({deck.flashcardCount || 0} cards)
                      </option>
                    ))}
                </select>
              </div>
            )}

            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
              <div className="text-sm font-mono">
                <span className="text-sky-blue">Card:</span> <span className="text-terminal-green">{formatTime(elapsedTime)}</span>{' '}
                <span className="text-sky-blue">Session:</span> <span className="text-terminal-green">{formatSessionTime(sessionStartTime)}</span>
              </div>
              <div className="text-trail-tan text-sm font-mono">
                Card {safeCurrentIndex + 1} of {dueFlashcards.length}
              </div>
            </div>
          </div>

          {/* Progress bar */}
          <div className="w-full bg-black border-2 border-sky-blue h-4">
            <div
              className="h-full bg-terminal-green transition-all duration-300"
              style={{ width: `${((safeCurrentIndex + 1) / dueFlashcards.length) * 100}%` }}
            />
          </div>
        </div>

        {/* Waveform Audio Player - With Timestamp Support */}
        {currentCard && currentCard.fullSongUrl && (
          <div className="mb-4">
            <WaveSurferPlayer
              key={`wavesurfer-${currentCard._id}-${currentCard.fullSongUrl}`}
              audioUrl={currentCard.fullSongUrl}
              regions={audioRegions}
              onRegionClick={handleRegionClick}
              height={128}
              wholeSongMode={playbackMode === 'wholeSong'}
              playbackMode={playbackMode}
              onModeSelect={(mode) => dispatch(setPlaybackMode(mode))}
            />
          </div>
        )}

        {/* Flashcard */}
        {currentCard && (
          <div className="mb-6">
            <FlashcardCard
              frontSpanish={currentCard.frontSpanish}
              backEnglish={currentCard.backEnglish}
              targetVerb={currentCard.targetVerb}
              sourceVerb={currentCard.sourceVerb}
              audioUrl={currentCard.audioUrl}
              extractedAudioUrl={currentCard.extractedAudioUrl}
              fullSongUrl={currentCard.fullSongUrl}
              flashcardId={currentCard._id}
              isFlipped={isFlipped}
              onFlip={() => setIsFlipped(!isFlipped)}
            />
          </div>
        )}

        {/* Source Song Link */}
        {currentCard && sourceSongData && (
          <div className="mb-4 text-center">
            <Link
              to="/app/songs/$generationId/create-flashcards"
              params={{ generationId: currentCard.sourceSongId! }}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-mono text-sky-blue hover:text-terminal-green border-2 border-sky-blue/30 hover:border-terminal-green/50 rounded transition-colors"
            >
              <Music className="w-4 h-4" />
              <span>View Source Song</span>
              {sourceSongData.presetSong && (
                <span className="text-trail-tan">• {sourceSongData.presetSong.title}</span>
              )}
            </Link>
          </div>
        )}

        {/* Grade Buttons - Disabled until flipped */}
        {currentCard && (
          <div
            className="mb-6 transition-all duration-300"
            style={!isFlipped ? { filter: 'grayscale(100%)', opacity: 0.5 } : {}}
          >
            <div className={`text-center font-mono font-bold mb-4 text-sm sm:text-base ${
              isFlipped ? 'text-sky-blue' : 'text-gray-500'
            }`}>
              How well did you know this?
            </div>
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-4 sm:gap-8">
              {[
                { grade: 0 as AnkiGrade, label: '0', desc: 'Forgot', color: 'bg-red-600' },
                { grade: 1 as AnkiGrade, label: '1', desc: 'Hard', color: 'bg-orange-400' },
                { grade: 2 as AnkiGrade, label: '2', desc: 'OK', color: 'bg-yellow-600' },
                { grade: 3 as AnkiGrade, label: '3', desc: 'Good', color: 'bg-blue-600' },
                { grade: 4 as AnkiGrade, label: '4', desc: 'Easy', color: 'bg-teal-600' },
                { grade: 5 as AnkiGrade, label: '5', desc: 'Perfect', color: 'bg-green-600' },
              ].map(({ grade, label, desc, color }) => (
                <button
                  key={grade}
                  onClick={() => isFlipped && handleGrade(grade)}
                  disabled={!isFlipped}
                  className={`${color} text-white font-bold font-mono text-lg sm:text-xl py-3 sm:py-5 border-4 border-dotted border-white rounded-lg
                    ${isFlipped ? 'hover:border-terminal-green cursor-pointer' : 'cursor-not-allowed'}
                    transition-all min-h-[44px] sm:min-h-[60px]`}
                >
                  <div className="text-2xl sm:text-3xl mb-1">{label}</div>
                  <div className="text-xs sm:text-sm">{desc}</div>
                </button>
              ))}
            </div>
            <div className={`text-center text-xs font-mono mt-2 ${
              !isFlipped ? 'text-gray-500' : 'text-transparent'
            }`}>
              {!isFlipped ? 'Flip the card to rate your knowledge' : '\u00A0'}
            </div>
          </div>
        )}

        {/* Anki Scheduling Info - Collapsible */}
        {currentCard && (
          <AnkiSchedulingInfo 
            card={currentCard} 
            isExpanded={isAnkiInfoExpanded}
            onToggle={() => setIsAnkiInfoExpanded(!isAnkiInfoExpanded)}
          />
        )}

        {/* Card Type Indicator */}
        {currentCard && (
          <div className="text-center text-trail-tan text-xs font-mono">
            Type: <span className="text-sky-blue uppercase">{currentCard.cardType}</span>
            {currentCard.priority !== 5 && (
              <> • Priority: <span className="text-terminal-green">{currentCard.priority}/10</span></>
            )}
          </div>
        )}
      </div>

      {/* Session History Sidebar */}
      <SessionHistorySidebar
        userId={userId}
        isOpen={isSidebarOpen}
        onClose={() => setIsSidebarOpen(false)}
      />
    </div>
  )
}

function AnkiSchedulingInfo({ 
  card, 
  isExpanded, 
  onToggle 
}: { 
  card: {
    easeFactor: number
    interval: number
    repetitions: number
    isLearned: boolean
    lastReviewDate?: number
  }
  isExpanded: boolean
  onToggle: () => void
}) {
  return (
    <div className="mb-6 terminal-border p-4 sm:p-6 bg-black">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between text-left mb-4 hover:opacity-80 transition-opacity"
        type="button"
      >
        <h2 className="text-xl font-bold text-terminal-green font-mono">
          [{isExpanded ? 'ANKI SCHEDULING INFO' : 'VOCAB STATS'}]
        </h2>
        <span className="text-terminal-green font-mono text-lg">
          {isExpanded ? '▼' : '►'}
        </span>
      </button>
      
      {isExpanded && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-black border-2 border-sky-blue p-3">
            <div className="text-sky-blue text-xs font-mono mb-1 uppercase tracking-wider">
              Ease Factor
            </div>
            <div className="text-terminal-green text-2xl font-bold font-mono">
              {card.easeFactor.toFixed(2)}
            </div>
            <div className="text-trail-tan text-xs font-mono mt-1">
              Min: 1.3
            </div>
          </div>
          <div className="bg-black border-2 border-sky-blue p-3">
            <div className="text-sky-blue text-xs font-mono mb-1 uppercase tracking-wider">
              Interval
            </div>
            <div className="text-terminal-green text-2xl font-bold font-mono">
              {card.interval}
            </div>
            <div className="text-trail-tan text-xs font-mono mt-1">
              {card.interval === 0 ? 'Due now' : `${card.interval} day${card.interval !== 1 ? 's' : ''}`}
            </div>
          </div>
          <div className="bg-black border-2 border-sky-blue p-3">
            <div className="text-sky-blue text-xs font-mono mb-1 uppercase tracking-wider">
              Repetitions
            </div>
            <div className="text-terminal-green text-2xl font-bold font-mono">
              {card.repetitions}
            </div>
            <div className="text-trail-tan text-xs font-mono mt-1">
              Consecutive correct
            </div>
          </div>
          <div className="bg-black border-2 border-sky-blue p-3">
            <div className="text-sky-blue text-xs font-mono mb-1 uppercase tracking-wider">
              Status
            </div>
            <div className={`text-2xl font-bold font-mono ${
              card.isLearned ? 'text-terminal-green' : 
              card.interval > 90 ? 'text-terminal-green' : 
              'text-trail-tan'
            }`}>
              {card.isLearned ? 'LEARNED' : 'ACTIVE'}
            </div>
            <div className="text-trail-tan text-xs font-mono mt-1">
              {card.lastReviewDate 
                ? `Last: ${new Date(card.lastReviewDate).toLocaleDateString()}`
                : 'New card'
              }
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

